import React, { useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import COLORS from '../../../../resources/style/Themes';

const CreateSupportLinkModal = ({ show, onHide, onCreate }) => {
  const [header, setHeader] = useState('');
  const [linkType, setLinkType] = useState('');
  const [name, setName] = useState('');
  const [sectionType, setSectionType] = useState('');
  const [url, setUrl] = useState('');
  const [languageCode, setLanguageCode] = useState(''); // New state for language code
  const [validation, setValidation] = useState({});

  const handleSubmit = () => {
    if (!validateFields()) {
      return;
    }
    const newSupportLink = {
      header: header,
      is_active: false,
      link_type: linkType,
      name: name,
      section_type: sectionType,
      url: url,
      language_code: languageCode,
    };

    onCreate(newSupportLink);
    resetValues();
    onHide();
  };

  const resetValues = () => {
    setHeader('');
    setLinkType('');
    setName('');
    setSectionType('');
    setUrl('');
    setLanguageCode(''); // Reset language code
    setValidation({});
  };

  const validateFields = () => {
    let isValid = true;
    const newValidation = {};

    if (!header || header.length === 0) {
      newValidation.header = 'error';
      isValid = false;
    }
    if (!name || name.length === 0) {
      newValidation.name = 'error';
      isValid = false;
    }
    if (!url || url.length === 0) {
      newValidation.url = 'error';
      isValid = false;
    }
    if (!linkType) {
      newValidation.linkType = 'error';
      isValid = false;
    }
    if (!sectionType) {
      newValidation.sectionType = 'error';
      isValid = false;
    }
    if (!languageCode || languageCode.length === 0) {
      newValidation.languageCode = 'error';
      isValid = false;
    }
    setValidation(newValidation);
    return isValid;
  };

  return (
    <Modal
      open={show}
      onClose={() => {
        onHide();
        resetValues();
      }}
      aria-labelledby="create-support-link-modal-title"
      aria-describedby="create-support-link-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          outline: 'none',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: COLORS.WHITE,
            p: 2,
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
          }}
        >
          <Typography
            id="create-support-link-modal-title"
            variant="h6"
            align="center"
          >
            Add New Support Link
          </Typography>
        </Box>

        {/* Modal Content */}
        <Box component="form" sx={{ p: 4 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Header"
              variant="outlined"
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              error={validation.header === 'error'}
              helperText={
                validation.header === 'error' ? 'Header cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={validation.name === 'error'}
              helperText={
                validation.name === 'error' ? 'Name cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="URL"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              error={validation.url === 'error'}
              helperText={
                validation.url === 'error' ? 'URL cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value)}
              displayEmpty
              error={validation.linkType === 'error'}
              inputProps={{ 'aria-label': 'Link Type' }}
            >
              <MenuItem value="" disabled>
                Select Link Type
              </MenuItem>
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="web_page">Web Page</MenuItem>
            </Select>
            {validation.linkType === 'error' && (
              <Typography variant="body2" color="error">
                Select a link type.
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value)}
              displayEmpty
              error={validation.sectionType === 'error'}
              inputProps={{ 'aria-label': 'Section Type' }}
            >
              <MenuItem value="" disabled>
                Select Section Type
              </MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="support">Support</MenuItem>
            </Select>
            {validation.sectionType === 'error' && (
              <Typography variant="body2" color="error">
                Select a section type.
              </Typography>
            )}
          </FormControl>
          {/* Language Code Dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              displayEmpty
              error={validation.languageCode === 'error'}
              inputProps={{ 'aria-label': 'Language Code' }}
            >
              <MenuItem value="" disabled>
                Select Language Code
              </MenuItem>
              <MenuItem value="dan">Danish</MenuItem>
              <MenuItem value="eng">English</MenuItem>
              <MenuItem value="fin">Finnish</MenuItem>
              <MenuItem value="fra">French</MenuItem>
              <MenuItem value="deu">German</MenuItem>
              <MenuItem value="ell">Greek</MenuItem>
              <MenuItem value="nob">Norwegian</MenuItem>
              <MenuItem value="pol">Polish</MenuItem>
              <MenuItem value="por">Portuguese</MenuItem>
              <MenuItem value="spa">Spanish</MenuItem>
              <MenuItem value="swe">Swedish</MenuItem>
            </Select>
            {validation.languageCode === 'error' && (
              <Typography variant="body2" color="error">
                Select a language code.
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Footer Section */}
        <Box sx={{ textAlign: 'right', p: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateSupportLinkModal;
